import React, { useState, useContext, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ClockIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  PhotoIcon,
  XMarkIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import { getDownloadURL, ref as storageRef, uploadString } from 'firebase/storage';
import { db, storage } from '../firebase';
import { doc, getDoc, updateDoc, collection, getDocs, writeBatch } from 'firebase/firestore';
import { UserContext } from '../App';
import Spinner from '../components/MatrixLoader';
import ZoomableImageModal from '../components/ZoomableImageModal';
import ConfigurableJournalInput from '../components/ConfigurableJournalInput';
import { isTapeReading } from '../utils/tradeHelpers';
import { buildAccountBalanceTimeline } from "../utils/accountBalanceUtils";
import { getClipboardImageFiles, readImageFilesAsDataUrls } from "../utils/imageInput";
import { getPnlTone } from "../utils/pnlTone";
import {
  getDurationSuggestion,
  getEditableDurationFromEntry,
  getFormAutoDuration,
} from '../utils/formAutoDuration';
import {
  CHART_DROP_HIGHLIGHT_STYLE,
  useChartDropZone,
  useDocumentImagePaste,
  useRememberedSectionOpen,
  useSubmitShortcut,
  useUnsavedChangesGuard,
} from '../utils/entryFormHooks';
import {
  getDefaultTradeEnvFields,
  initializeTradeEnvState,
  resolveTradeEnvFields,
} from "../utils/tradeEnvFields";
import { buildTradeEnvUpdate, readTradeEnvFromEntry } from "../utils/tradeEnvAlignment";
import {
  getDefaultJournalFields,
  getJournalField,
  resolveJournalFields,
} from '../utils/journalFields';

const tickerOptions = ["MES", "MNQ", "BTC", "GL", "SL", "MYM"];
function Accordion({ title, icon, defaultOpen = false, children }) {
  const { currentUser } = useContext(UserContext) || {};
  // Same remembered state as the Add Entry form, per user on this device.
  const [isOpen, toggleOpen] = useRememberedSectionOpen(title, defaultOpen, currentUser?.uid);
  
  return (
    <div className="bg-white dark:bg-[#1F2937] rounded-card border border-[#E5E7EB] dark:border-[#374151] shadow-subtle overflow-hidden">
      <button
        type="button"
        aria-expanded={isOpen}
        onClick={toggleOpen}
        className="w-full p-4 flex items-center justify-between hover:bg-[#F9FAFB] dark:hover:bg-[#374151] transition-colors"
      >
        <div className="flex items-center gap-3">
          {icon && <div className="p-2 bg-[#F3F4F6] dark:bg-[#374151] rounded-card">{icon}</div>}
          <span className="text-lg font-semibold text-[#111827] dark:text-[#F9FAFB]">{title}</span>
        </div>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronDownIcon className="w-5 h-5 text-[#6B7280] dark:text-[#9CA3AF]" />
        </motion.div>
      </button>
      <motion.div
        initial={false}
        animate={{ height: isOpen ? "auto" : 0 }}
        transition={{ duration: 0.3 }}
        className="overflow-hidden"
      >
        <div className="p-6 border-t border-[#E5E7EB] dark:border-[#374151]">
          {children}
        </div>
      </motion.div>
    </div>
  );
}

function SegmentedPill({ value, onChange, label }) {
  const getButtonClass = (buttonValue) => {
    const baseClass = "flex-1 h-12 text-sm font-medium transition-colors";
    if (value === buttonValue) {
      if (buttonValue === true) {
        return `${baseClass} theme-success-soft`;
      } else if (buttonValue === false) {
        return `${baseClass} theme-danger-soft`;
      } else {
        return `${baseClass} bg-[#F3F4F6] dark:bg-[#374151] text-[#6B7280] dark:text-[#D1D5DB]`;
      }
    } else {
      return `${baseClass} bg-white dark:bg-[#1F2937] text-[#6B7280] dark:text-[#9CA3AF] hover:bg-[#F9FAFB] dark:hover:bg-[#374151] border border-[#E5E7EB] dark:border-[#374151]`;
    }
  };

  return (
    <div className="flex flex-col gap-2">
      {label && (
        <label className="text-sm font-medium text-[#374151] dark:text-[#D1D5DB] flex items-center gap-2">
          {label}
        </label>
      )}
      <div className="flex rounded-btn overflow-hidden border border-[#E5E7EB] dark:border-[#374151]">
        <button
          type="button"
          onClick={() => onChange(true)}
          className={getButtonClass(true)}
        >
          Yes
        </button>
        <button
          type="button"
          onClick={() => onChange(false)}
          className={`${getButtonClass(false)} border-l border-r border-[#E5E7EB] dark:border-[#374151]`}
        >
          No
        </button>
        <button
          type="button"
          onClick={() => onChange(null)}
          className={getButtonClass(null)}
        >
          N/A
        </button>
      </div>
    </div>
  );
}

function trimFirestoreDoc(doc) {
  const MAX_STRING = 1000000;
  const MAX_ARRAY = 1000;
  const MAX_FIELD_BYTES = 1048487;
  function trim(obj) {
    if (Array.isArray(obj)) {
      let arr = obj.slice(0, MAX_ARRAY);
      arr = arr.map(trim);
      if (JSON.stringify(arr).length > MAX_FIELD_BYTES) return undefined;
      return arr;
    } else if (typeof obj === 'string') {
      let s = obj.slice(0, MAX_STRING);
      if (s.length > MAX_FIELD_BYTES) return undefined;
      return s;
    } else if (typeof obj === 'object' && obj !== null) {
      const out = {};
      for (const k in obj) {
        const trimmed = trim(obj[k]);
        if (trimmed !== undefined) out[k] = trimmed;
      }
      if (JSON.stringify(out).length > MAX_FIELD_BYTES) return undefined;
      return out;
    } else {
      return obj;
    }
  }
  return trim(doc);
}

async function syncAccountBalancesForAccount(dbInstance, userId, accountId) {
  const entriesCol = collection(dbInstance, 'users', userId, 'accounts', accountId, 'entries');
  const snap = await getDocs(entriesCol);
  const entries = snap.docs.map((entryDoc) => ({ id: entryDoc.id, ...entryDoc.data() }));
  if (!entries.length) return;

  const timeline = buildAccountBalanceTimeline(entries);
  const batchLimit = 450;
  let currentBatch = writeBatch(dbInstance);
  let pendingInBatch = 0;
  const commits = [];

  timeline.forEach(({ entry, accountBalance }) => {
    const currentStored = Number(entry.accountBalance);
    const normalizedStored = Number.isFinite(currentStored)
      ? Math.round(currentStored * 100) / 100
      : null;
    if (normalizedStored === accountBalance) return;

    const entryRef = doc(dbInstance, 'users', userId, 'accounts', accountId, 'entries', entry.id);
    currentBatch.update(entryRef, { accountBalance });
    pendingInBatch += 1;

    if (pendingInBatch >= batchLimit) {
      commits.push(currentBatch.commit());
      currentBatch = writeBatch(dbInstance);
      pendingInBatch = 0;
    }
  });

  if (pendingInBatch > 0) {
    commits.push(currentBatch.commit());
  }

  if (commits.length > 0) {
    await Promise.all(commits);
  }
}

function getEntryScreenshots(entryData) {
  if (Array.isArray(entryData?.screenshots) && entryData.screenshots.length > 0) {
    return [...entryData.screenshots];
  }
  if (Array.isArray(entryData?.images) && entryData.images.length > 0) {
    return [...entryData.images];
  }
  if (Array.isArray(entryData?.imageUrls) && entryData.imageUrls.length > 0) {
    return [...entryData.imageUrls];
  }
  if (entryData?.image) return [entryData.image];
  if (entryData?.screenshot) return [entryData.screenshot];
  if (entryData?.imageUrl) return [entryData.imageUrl];
  return [];
}

const EditTradePage = () => {
  const { entryId } = useParams();
  const { currentUser, triggerDataRefresh } = useContext(UserContext);
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [entry, setEntry] = useState(null);
  const [isOrphaned, setIsOrphaned] = useState(false);
  
  // Form state
  const [form, setForm] = useState({
    title: "",
    tickerTraded: "",
    pnl: "",
    rr: "",
    notes: "",
    duration: "",
    entryTime: "",
    exitTime: "",
    tradeSession: "NY-AM",
  });
  const pnlTone = getPnlTone(form.pnl);
  // Snapshot of the form right after the entry loaded; anything different is unsaved.
  const [loadedSnapshot, setLoadedSnapshot] = useState(null);
  const [pdArray, setPdArray] = useState("");
  const [target, setTarget] = useState("");
  const [economicRelease, setEconomicRelease] = useState("");
  const [screenshots, setScreenshots] = useState([]);
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [tickerSuggestions, setTickerSuggestions] = useState([]);
  const [showTickerSuggestions, setShowTickerSuggestions] = useState(false);
  const [tradeRating, setTradeRating] = useState('');
  const fileInputRef = useRef(null);
  const formRef = useRef(null);
  // True when the duration is the user's own value; it is then never recalculated over.
  const [durationEdited, setDurationEdited] = useState(false);
  
  // Helper function to convert year/month/day to YYYY-MM-DD format for date input
  const getInitialDate = useCallback((entryData) => {
    if (entryData && entryData.year && entryData.month && entryData.day) {
      // Convert to YYYY-MM-DD format for date input
      const year = String(entryData.year).padStart(4, '0');
      const month = String(entryData.month).padStart(2, '0');
      const day = String(entryData.day).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    // Default to current date if no date fields exist
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  const getTradeEntryDate = useCallback((entryData) => {
    if (entryData && entryData.entryYear && entryData.entryMonth && entryData.entryDay) {
      const year = String(entryData.entryYear).padStart(4, '0');
      const month = String(entryData.entryMonth).padStart(2, '0');
      const day = String(entryData.entryDay).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    return getInitialDate(entryData);
  }, [getInitialDate]);
  
  const [logDate, setLogDate] = useState("");
  const [swingEntryDate, setSwingEntryDate] = useState("");
  
  // Trading style state: 'day' or 'swing'
  const [tradingStyle, setTradingStyle] = useState('day');
  
  // Swing trading exit date state
  const [exitDate, setExitDate] = useState('');
  
  // Helper function to convert exit year/month/day to YYYY-MM-DD format
  const getExitDate = useCallback((entryData) => {
    if (entryData && entryData.exitYear && entryData.exitMonth && entryData.exitDay) {
      const year = String(entryData.exitYear).padStart(4, '0');
      const month = String(entryData.exitMonth).padStart(2, '0');
      const day = String(entryData.exitDay).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    return '';
  }, []);
  
  const defaultTradeEnvFields = getDefaultTradeEnvFields();
  const defaultJournalFields = getDefaultJournalFields();
  
  const [journalFields, setJournalFields] = useState(defaultJournalFields);
  const [tradeEnvFields, setTradeEnvFields] = useState(defaultTradeEnvFields);
  
  const [tradeEnv, setTradeEnv] = useState(() => initializeTradeEnvState(defaultTradeEnvFields));
  // Trade-environment answers as loaded, so untouched answers are written back exactly as stored.
  const loadedTradeEnvRef = useRef({});

  // Load custom field definitions
  useEffect(() => {
    if (!currentUser) return;
    
    const loadSettings = async () => {
      try {
        const prefsRef = doc(db, 'users', currentUser.uid, 'preferences', 'settings');
        const prefsSnap = await getDoc(prefsRef);
        
        if (prefsSnap.exists()) {
          const data = prefsSnap.data();
          const fieldsToUse = resolveTradeEnvFields(data);
          setJournalFields(resolveJournalFields(data));
          setTradeEnvFields(fieldsToUse);
          setTradeEnv(initializeTradeEnvState(fieldsToUse));
        }
      } catch (error) {
        console.error('Error loading settings:', error);
      }
    };
    
    loadSettings();
  }, [currentUser]);
  
  // Load entry data
  useEffect(() => {
    if (!currentUser || !entryId) return undefined;
    // Settings can arrive after the first load starts; only the latest load may fill the form.
    let cancelled = false;
    
    const loadEntry = async () => {
      setLoading(true);
      try {
        // Try all accounts to find the entry
        const accountsCol = collection(db, 'users', currentUser.uid, 'accounts');
        const accountsSnap = await getDocs(accountsCol);
        if (cancelled) return;
        const accounts = accountsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        // Try each account
        for (const account of accounts) {
          const entryRef = doc(db, 'users', currentUser.uid, 'accounts', account.id, 'entries', entryId);
          const entrySnap = await getDoc(entryRef);
          if (cancelled) return;
          
          if (entrySnap.exists()) {
            const entryData = { ...entrySnap.data(), id: entrySnap.id, accountId: account.id };
            setEntry(entryData);
            setIsOrphaned(false);
            
            // Initialize dates from entry data
            setLogDate(getInitialDate(entryData));
            setSwingEntryDate(getTradeEntryDate(entryData));
            
            // Load trading style (default to 'day' if not present)
            setTradingStyle(entryData.tradingStyle || 'day');
            
            // Load exit date for swing trading
            setExitDate(getExitDate(entryData));
            
            // Entries saved without a duration get the one calculated from their times.
            const loadedDuration = getEditableDurationFromEntry(entryData);
            setDurationEdited(loadedDuration.edited);
            // Populate form
            setForm({
              title: entryData.title || "",
              tickerTraded: entryData.tickerTraded || "",
              pnl: entryData.pnl !== undefined ? String(entryData.pnl) : "",
              rr: entryData.rr || "",
              notes: entryData.notes || "",
              duration: loadedDuration.duration,
              entryTime: entryData.entryTime || "",
              exitTime: entryData.exitTime || "",
              tradeSession: entryData.tradeSession || "NY-AM",
            });
            setPdArray(entryData.pdArray || "");
            setTarget(entryData.target || "");
            setEconomicRelease(entryData.economicRelease || "");
            setTradeRating(entryData.tradeRating || "");
            setScreenshots(getEntryScreenshots(entryData));

            // Populate trade environment fields (00:00 / 8:30 answers decoded for the form)
            const envState = readTradeEnvFromEntry(tradeEnvFields, entryData);
            loadedTradeEnvRef.current = envState;
            setTradeEnv(envState);

            setLoading(false);
            return;
          }
        }
        
        // Try orphaned entry
        const orphanedRef = doc(db, 'users', currentUser.uid, 'orphanedEntries', entryId);
        const orphanedSnap = await getDoc(orphanedRef);
        if (cancelled) return;
        
        if (orphanedSnap.exists()) {
          const entryData = { ...orphanedSnap.data(), id: orphanedSnap.id };
          setEntry(entryData);
          setIsOrphaned(true);
          
          // Initialize dates from entry data
          setLogDate(getInitialDate(entryData));
          setSwingEntryDate(getTradeEntryDate(entryData));

          // Load trading style (default to 'day' if not present)
          setTradingStyle(entryData.tradingStyle || 'day');

          // Load exit date for swing trading
          setExitDate(getExitDate(entryData));
          
          // Entries saved without a duration get the one calculated from their times.
          const loadedDuration = getEditableDurationFromEntry(entryData);
          setDurationEdited(loadedDuration.edited);
          // Populate form (same as above)
          setForm({
            title: entryData.title || "",
            tickerTraded: entryData.tickerTraded || "",
            pnl: entryData.pnl !== undefined ? String(entryData.pnl) : "",
            rr: entryData.rr || "",
            notes: entryData.notes || "",
            duration: loadedDuration.duration,
            entryTime: entryData.entryTime || "",
            exitTime: entryData.exitTime || "",
            tradeSession: entryData.tradeSession || "NY-AM",
          });
          setPdArray(entryData.pdArray || "");
          setTarget(entryData.target || "");
          setEconomicRelease(entryData.economicRelease || "");
          setTradeRating(entryData.tradeRating || "");
          setScreenshots(getEntryScreenshots(entryData));
          
          const envState = readTradeEnvFromEntry(tradeEnvFields, entryData);
          loadedTradeEnvRef.current = envState;
          setTradeEnv(envState);
          
          setLoading(false);
          return;
        }
        
        // Entry not found
        alert('Entry not found');
        navigate('/context');
      } catch (error) {
        if (cancelled) return;
        console.error('Error loading entry:', error?.message || error);
        alert('Failed to load entry. Please try again.');
        navigate('/context');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    
    loadEntry();
    return () => { cancelled = true; };
  }, [currentUser, entryId, tradeEnvFields, navigate, getInitialDate, getTradeEntryDate, getExitDate]);
  
  // Helper to auto-format time as HH:MM (24-hour format)
  function formatTimeInput(value) {
    let v = value.replace(/\D/g, '');
    if (v.length <= 2) return v;
    if (v.length === 3) return v[0] + ':' + v.slice(1);
    if (v.length === 4) return v.slice(0, 2) + ':' + v.slice(2);
    return v.slice(0, 2) + ':' + v.slice(2, 4);
  }
  
  // Duration the form fills in from the entry/exit dates and times (null = not enough yet).
  const autoDurationFor = (times, entryDate, exitDt) => getFormAutoDuration({
    tradingStyle,
    entryDate,
    exitDate: exitDt,
    entryTime: times.entryTime,
    exitTime: times.exitTime,
  });

  const withAutoDuration = (updated, entryDate, exitDt) => {
    if (durationEdited) return updated;
    const autoDuration = autoDurationFor(updated, entryDate, exitDt);
    return autoDuration === null ? updated : { ...updated, duration: autoDuration };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'entryTime' || name === 'exitTime') {
      const formatted = formatTimeInput(value);
      setForm((prev) => withAutoDuration({ ...prev, [name]: formatted }, swingEntryDate || logDate, exitDate));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleDurationChange = (e) => {
    const { value } = e.target;
    setForm((prev) => ({ ...prev, duration: value }));
    setDurationEdited(value.trim() !== '');
  };
  
  // Handle main entry date change (used for both day and swing trading)
  const handleMainEntryDateChange = (e) => {
    const newLogDate = e.target.value;
    setLogDate(newLogDate);
    if (tradingStyle === 'swing' && !swingEntryDate) {
      setForm(prev => withAutoDuration(prev, newLogDate, exitDate));
    }
  };
  
  // Handle swing trading entry date change (separate from main date field)
  const handleSwingEntryDateChange = (e) => {
    const newEntryDate = e.target.value;
    setSwingEntryDate(newEntryDate);
    setForm(prev => withAutoDuration(prev, newEntryDate, exitDate));
  };
  
  // Handle exit date change for swing trading
  const handleExitDateChange = (e) => {
    const newExitDate = e.target.value;
    setExitDate(newExitDate);
    setForm(prev => withAutoDuration(prev, swingEntryDate || logDate, newExitDate));
  };

  const autoDuration = autoDurationFor(form, swingEntryDate || logDate, exitDate);
  const durationSuggestion = getDurationSuggestion(form.duration, autoDuration);
  const applySuggestedDuration = () => {
    setForm((prev) => ({ ...prev, duration: durationSuggestion }));
    setDurationEdited(false);
  };

  const handleFiles = useCallback((files) => {
    readImageFilesAsDataUrls(files).then((images) => {
      if (images.length > 0) {
        setScreenshots((prev) => [...prev, ...images]);
      }
    });
  }, []);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    handleFiles(files);
    e.target.value = null;
  };

  // Drag-and-drop onto the chart area and Ctrl/Cmd+V anywhere in the form both
  // add charts through the same preview/upload path as the file picker.
  const { isDragging: isDraggingChart, dropZoneProps: chartDropZoneProps } = useChartDropZone(handleFiles);
  useDocumentImagePaste(handleFiles, !loading);
  useSubmitShortcut(formRef);

  const handlePasteImages = (e) => {
    const files = getClipboardImageFiles(e);
    if (files.length === 0) return;
    e.preventDefault();
    e.stopPropagation();
    handleFiles(files);
  };

  const handleRemoveScreenshot = (indexToRemove) => {
    setScreenshots((prev) => prev.filter((_, index) => index !== indexToRemove));
    setSelectedImageIndex((prev) => {
      if (prev < indexToRemove) return prev;
      return Math.max(0, prev - 1);
    });
  };
  
  const formSnapshot = JSON.stringify({
    form, pdArray, target, economicRelease, screenshots, tradeRating, tradeEnv,
    logDate, swingEntryDate, tradingStyle, exitDate,
  });
  useEffect(() => {
    if (loading) {
      setLoadedSnapshot(null);
    } else if (entry && loadedSnapshot === null) {
      setLoadedSnapshot(formSnapshot);
    }
  }, [loading, entry, loadedSnapshot, formSnapshot]);
  const isDirty = loadedSnapshot !== null && formSnapshot !== loadedSnapshot;
  const leaveIfConfirmed = useUnsavedChangesGuard(isDirty);
  const handleClose = () => leaveIfConfirmed(() => navigate('/context'));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving || !entry) return;
    setSaving(true);
    
    try {
      // Determine entry location
      const entryRef = isOrphaned
        ? doc(db, 'users', currentUser.uid, 'orphanedEntries', entryId)
        : doc(db, 'users', currentUser.uid, 'accounts', entry.accountId, 'entries', entryId);
      
      // Parse logDate to extract year, month, day (logDate is in YYYY-MM-DD format)
      // Parse date string directly to avoid timezone issues
      const [year, month, day] = logDate.split('-').map(Number);
      const entryYear = String(year);
      const entryMonth = String(month);
      const entryDay = String(day);
      
      // Prepare exit date fields for swing trading
      let exitYear = null;
      let exitMonth = null;
      let exitDay = null;
      if (tradingStyle === 'swing' && exitDate) {
        const [exYear, exMonth, exDay] = exitDate.split('-').map(Number);
        exitYear = String(exYear);
        exitMonth = String(exMonth);
        exitDay = String(exDay);
      }

      let tradeEntryYear = null;
      let tradeEntryMonth = null;
      let tradeEntryDay = null;
      if (tradingStyle === 'swing') {
        const swingDate = swingEntryDate || logDate;
        if (swingDate) {
          const [enYear, enMonth, enDay] = swingDate.split('-').map(Number);
          tradeEntryYear = String(enYear);
          tradeEntryMonth = String(enMonth);
          tradeEntryDay = String(enDay);
        }
      }
      const newPnl = Number(form.pnl) || 0;
      const screenshotAccountId = entry.accountId || entry.originalAccountId || 'orphaned';
      const uploadBatchId = Date.now();
      const uploadedScreenshots = await Promise.all(
        screenshots.map(async (src, index) => {
          if (typeof src !== 'string') return null;
          if (src.startsWith('http')) return src;
          if (!src.startsWith('data:')) return src;

          const imageRef = storageRef(
            storage,
            `screenshots/${currentUser.uid}/${screenshotAccountId}/${uploadBatchId}-${index}.jpg`
          );
          await uploadString(imageRef, src, 'data_url');
          return getDownloadURL(imageRef);
        })
      );
      const normalizedScreenshots = uploadedScreenshots.filter(Boolean);
      setScreenshots(normalizedScreenshots);
      setEntry((prev) => (prev ? { ...prev, screenshots: normalizedScreenshots } : prev));
      
      // Prepare update data
      const updateData = {
        tradeRating: tradeRating || "",
        title: form.title,
        tickerTraded: form.tickerTraded,
        pnl: newPnl,
        rr: form.rr,
        notes: form.notes,
        duration: form.duration,
        entryTime: form.entryTime,
        exitTime: form.exitTime,
        tradeSession: form.tradeSession,
        economicRelease: economicRelease,
        pdArray: pdArray || "",
        target: target || "",
        year: entryYear,
        month: entryMonth,
        day: entryDay,
        screenshots: normalizedScreenshots,
        accountBalance: entry.accountBalance,
        tradingStyle: tradingStyle,
        ...(tradingStyle === 'swing' && tradeEntryYear && tradeEntryMonth && tradeEntryDay ? {
          entryYear: tradeEntryYear,
          entryMonth: tradeEntryMonth,
          entryDay: tradeEntryDay,
        } : {}),
        ...(tradingStyle === 'swing' && exitYear && exitMonth && exitDay ? {
          exitYear: exitYear,
          exitMonth: exitMonth,
          exitDay: exitDay,
        } : {}),
      };
      
      // Remove exit date fields if switching from swing to day trading
      if (tradingStyle === 'day' && entry.exitYear) {
        updateData.exitYear = null;
        updateData.exitMonth = null;
        updateData.exitDay = null;
      }

      if (tradingStyle === 'day' && entry.entryYear) {
        updateData.entryYear = null;
        updateData.entryMonth = null;
        updateData.entryDay = null;
      }

      // If staying in swing trading but user cleared the exit date, also clear stored exit fields
      if (tradingStyle === 'swing' && !exitDate && entry.exitYear) {
        updateData.exitYear = null;
        updateData.exitMonth = null;
        updateData.exitDay = null;
      }

      if (tradingStyle === 'swing' && !swingEntryDate && entry.entryYear) {
        updateData.entryYear = null;
        updateData.entryMonth = null;
        updateData.entryDay = null;
      }
      
      // Trade environment fields (incl. the 00:00 / 8:30 open answers): only enabled
      // questions are written; untouched answers go back exactly as stored, changed
      // 00:00 / 8:30 answers are stored as 'above' / 'below' / null.
      Object.assign(
        updateData,
        buildTradeEnvUpdate(tradeEnvFields, tradeEnv, loadedTradeEnvRef.current, entry)
      );
      
      // Trim the update data
      const trimmedData = trimFirestoreDoc(updateData);
      
      // Update Firestore
      await updateDoc(entryRef, trimmedData);

      // Keep the full ledger synchronized after any edit to avoid stale downstream balances.
      if (!isOrphaned && entry.accountId) {
        await syncAccountBalancesForAccount(db, currentUser.uid, entry.accountId);
      }
      
      // Trigger data refresh
      triggerDataRefresh();
      
      // Return to the exact entry so the Data page can restore its position.
      navigate(`/context?entryId=${encodeURIComponent(entryId)}`);
    } catch (error) {
      console.error('Error updating entry:', error);
      alert('Failed to update entry. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  
  const sessionField = getJournalField(journalFields, 'tradeSession');
  const economicReleaseField = getJournalField(journalFields, 'economicRelease');
  const pdArrayField = getJournalField(journalFields, 'pdArray');
  const targetField = getJournalField(journalFields, 'target');

  if (loading) {
    return (
      <div className="fixed inset-0 z-[70] flex items-center justify-center bg-[#FAFAFA] dark:bg-[#111827]">
        <Spinner size={48} />
      </div>
    );
  }
  
  return (
    <>
      <form ref={formRef} onSubmit={handleSubmit} className="fixed inset-0 z-[70] flex flex-col bg-[#FAFAFA] dark:bg-[#111827]">
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white dark:bg-[#1F2937] max-w-none w-screen h-screen rounded-none flex flex-col shadow-elevated"
        >
        {/* Sticky Header with X button */}
        <div className="sticky top-0 z-[70] bg-white dark:bg-[#1F2937] p-4 flex justify-between items-center border-b border-[#E5E7EB] dark:border-[#374151] rounded-none">
          <h2 className="text-lg sm:text-xl font-semibold text-[#111827] dark:text-[#F9FAFB]">Edit Trade</h2>
          {/* Trade Rating */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-[#374151] dark:text-[#D1D5DB]">Rating</span>
            <div className="flex rounded-btn overflow-hidden border border-[#E5E7EB] dark:border-[#374151]">
              {['A', 'B', 'C', 'D'].map(grade => (
                <button
                  key={grade}
                  type="button"
                  onClick={() => setTradeRating(prev => prev === grade ? '' : grade)}
                  className={`w-10 h-10 text-sm font-bold transition-colors ${
                    tradeRating === grade
                      ? grade === 'A' ? 'theme-success-action'
                      : grade === 'B' ? 'bg-[#3B82F6] text-white'
                      : grade === 'C' ? 'bg-[#F59E0B] text-white'
                      : 'theme-loss-action'
                      : 'bg-white dark:bg-[#1F2937] text-[#6B7280] dark:text-[#9CA3AF] hover:bg-[#F9FAFB] dark:hover:bg-[#374151]'
                  }`}
                >
                  {grade}
                </button>
              ))}
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="p-2 hover:bg-[#F3F4F6] dark:hover:bg-[#374151] rounded-card transition-colors"
            title="Close"
          >
            <XMarkIcon className="w-6 h-6 text-[#111827] dark:text-[#F9FAFB]" />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6" style={{ paddingBottom: '300px' }}>
          <div className="flex flex-col pb-64">
            {/* Title Field with Date Picker */}
            <div className="mb-6">
              <div className="flex flex-col sm:flex-row items-start gap-3">
                <div className="flex flex-col">
                  <label className="block text-sm font-medium text-[#374151] dark:text-[#D1D5DB] mb-2 ml-[10px]">Title</label>
                  <input 
                    name="title" 
                    type="text" 
                    value={form.title} 
                    onChange={handleChange} 
                    className="w-full sm:w-64 bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] p-3 rounded-card border border-[#E5E7EB] dark:border-[#374151] focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6] focus:outline-none transition-all text-sm font-normal" 
                    placeholder="Title for trade..." 
                  />
                </div>
                <div className="flex flex-col">
                  <label className="block text-sm font-medium text-[#374151] dark:text-[#D1D5DB] mb-2 ml-[10px]">
                    {tradingStyle === 'swing' ? 'Log Date' : 'Date'}
                  </label>
                  <input
                    type="date"
                    value={logDate}
                    onChange={handleMainEntryDateChange}
                    className="w-full sm:w-auto bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] p-3 rounded-card border border-[#E5E7EB] dark:border-[#374151] focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6] focus:outline-none transition-all text-sm font-normal [&::-webkit-calendar-picker-indicator]:cursor-pointer dark:[&::-webkit-calendar-picker-indicator]:invert"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="block text-sm font-medium text-[#374151] dark:text-[#D1D5DB] mb-2 ml-[10px]">Trading Style</label>
                  <select
                    value={tradingStyle}
                    onChange={(e) => {
                      setTradingStyle(e.target.value);
                      // Reset exit date when switching modes
                      if (e.target.value === 'day') {
                        setExitDate('');
                      }
                    }}
                    className="bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] p-3 rounded-card border border-[#E5E7EB] dark:border-[#374151] focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6] focus:outline-none transition-all text-sm font-normal"
                  >
                    <option value="day">Day Trading</option>
                    <option value="swing">Swing Trading</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Trade Details Section */}
            <div className="bg-white dark:bg-[#1F2937] rounded-card p-6 border border-[#E5E7EB] dark:border-[#374151] shadow-subtle mb-6">
              <h3 className="text-lg font-semibold text-[#111827] dark:text-[#F9FAFB] mb-4">Trade Details</h3>
              <div className="space-y-4">
                {/* Row 1: Ticker, P&L, R:R (P&L and R:R hidden for tape readings) */}
                <div className={`grid gap-4 ${isTapeReading(entry || {}) ? 'grid-cols-1' : 'grid-cols-2 sm:grid-cols-3'}`}>
                  <div data-testid="ticker-field" className={`relative flex flex-col gap-2 ${isTapeReading(entry || {}) ? '' : 'col-span-2 sm:col-span-1'}`}>
                    <label className="text-xs font-medium text-[#374151] dark:text-[#D1D5DB]">Ticker</label>
                    <input
                      name="tickerTraded"
                      type="text"
                      value={form.tickerTraded}
                      onChange={(e) => {
                        const value = e.target.value.toUpperCase();
                        setForm(prev => ({ ...prev, tickerTraded: value }));
                        if (value) {
                          const filtered = tickerOptions.filter(opt => 
                            opt.toUpperCase().startsWith(value.toUpperCase())
                          );
                          setTickerSuggestions(filtered);
                          setShowTickerSuggestions(filtered.length > 0);
                        } else {
                          setTickerSuggestions([]);
                          setShowTickerSuggestions(false);
                        }
                      }}
                      onFocus={() => {
                        if (form.tickerTraded) {
                          const filtered = tickerOptions.filter(opt => 
                            opt.toUpperCase().startsWith(form.tickerTraded.toUpperCase())
                          );
                          setTickerSuggestions(filtered);
                          setShowTickerSuggestions(filtered.length > 0);
                        } else {
                          setTickerSuggestions(tickerOptions);
                          setShowTickerSuggestions(true);
                        }
                      }}
                      onBlur={() => {
                        setTimeout(() => setShowTickerSuggestions(false), 200);
                      }}
                      className="w-full bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] p-2 rounded-card border border-[#E5E7EB] dark:border-[#374151] focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6] focus:outline-none transition-all text-sm font-normal"
                      placeholder="Enter ticker..."
                    />
                    {showTickerSuggestions && tickerSuggestions.length > 0 && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-[#1F2937] border border-[#E5E7EB] dark:border-[#374151] rounded-card shadow-elevated z-50 max-h-40 overflow-y-auto">
                        {tickerSuggestions.map(opt => (
                          <button
                            key={opt}
                            type="button"
                            onClick={() => {
                              setForm(prev => ({ ...prev, tickerTraded: opt }));
                              setShowTickerSuggestions(false);
                            }}
                            className="w-full text-left px-3 py-2 hover:bg-[#F3F4F6] dark:hover:bg-[#374151] text-sm text-[#111827] dark:text-[#F9FAFB]"
                          >
                            {opt}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {!isTapeReading(entry || {}) && (
                    <>
                      <div className="flex flex-col gap-2">
                        <label className="text-xs font-medium text-[#374151] dark:text-[#D1D5DB]">P&L</label>
                        <div className="relative">
                          <CurrencyDollarIcon
                            data-pnl-tone={pnlTone}
                            className="pnl-tone-text absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#6B7280] dark:text-[#9CA3AF]"
                          />
                          <input 
                            name="pnl" 
                            type="number" 
                            step="0.01" 
                            data-pnl-tone={pnlTone}
                            value={form.pnl} 
                            onChange={handleChange} 
                            onWheel={(e) => e.target.blur()} 
                            className="pnl-tone-input w-full bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] pl-10 pr-3 py-2 rounded-card border border-[#E5E7EB] dark:border-[#374151] focus:ring-2 focus:outline-none transition-all text-sm font-normal"
                            placeholder="$" 
                          />
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className="text-xs font-medium text-[#374151] dark:text-[#D1D5DB]">R:R</label>
                        <input name="rr" type="number" step="0.01" value={form.rr} onChange={handleChange} onWheel={(e) => e.target.blur()} className="w-full bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] p-2 rounded-card border border-[#E5E7EB] dark:border-[#374151] focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6] focus:outline-none transition-all text-sm font-normal" placeholder="e.g. 2.5" />
                      </div>
                    </>
                  )}
                </div>
                {/* Row 2: Entry/Exit Time and Duration - Conditional based on trading style */}
                {tradingStyle === 'day' ? (
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                    <div className="flex flex-col gap-2">
                      <label className="text-xs font-medium text-[#374151] dark:text-[#D1D5DB]">Entry Time</label>
                      <div className="relative">
                        <ClockIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#6B7280] dark:text-[#9CA3AF]" />
                        <input name="entryTime" type="text" value={form.entryTime} onChange={handleChange} className="w-full bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] pl-10 pr-3 py-2 rounded-card border border-[#E5E7EB] dark:border-[#374151] focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6] focus:outline-none transition-all text-sm font-normal" placeholder="e.g. 09:30" />
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-xs font-medium text-[#374151] dark:text-[#D1D5DB]">Exit Time</label>
                      <div className="relative">
                        <ClockIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#6B7280] dark:text-[#9CA3AF]" />
                        <input name="exitTime" type="text" value={form.exitTime} onChange={handleChange} className="w-full bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] pl-10 pr-3 py-2 rounded-card border border-[#E5E7EB] dark:border-[#374151] focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6] focus:outline-none transition-all text-sm font-normal" placeholder="e.g. 14:45" />
                      </div>
                    </div>
                    <div data-testid="duration-field" className="col-span-2 flex flex-col gap-2 sm:col-span-1">
                      <div className="flex items-center justify-between gap-2">
                        <label htmlFor="edit-duration" className="text-xs font-medium text-[#374151] dark:text-[#9CA3AF]">Duration</label>
                        {durationSuggestion && (
                          <button type="button" onClick={applySuggestedDuration} className="text-xs font-medium text-[#3B82F6] hover:underline dark:text-[#60A5FA]">
                            Use {durationSuggestion}
                          </button>
                        )}
                      </div>
                      <input id="edit-duration" name="duration" type="text" value={form.duration} onChange={handleDurationChange} className="w-full bg-[#F9FAFB] dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] p-2 rounded-card border border-[#E5E7EB] dark:border-[#374151] focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6] focus:outline-none transition-all text-sm font-normal" placeholder="Auto-calculated" />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Entry Date/Time for Swing Trading */}
                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                      <div className="flex flex-col gap-2">
                        <label className="text-xs font-medium text-[#374151] dark:text-[#D1D5DB]">Entry Date</label>
                        <div className="relative">
                          <CalendarIcon className="absolute left-3 top-1/2 hidden sm:block transform -translate-y-1/2 w-4 h-4 text-[#6B7280] dark:text-[#9CA3AF]" />
                          <input 
                            type="date" 
                            value={swingEntryDate} 
                            onChange={handleSwingEntryDateChange}
                            className="w-full bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] pl-3 sm:pl-10 pr-3 py-2 rounded-card border border-[#E5E7EB] dark:border-[#374151] focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6] focus:outline-none transition-all text-sm font-normal [&::-webkit-calendar-picker-indicator]:cursor-pointer dark:[&::-webkit-calendar-picker-indicator]:invert"
                          />
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className="text-xs font-medium text-[#374151] dark:text-[#D1D5DB]">Entry Time</label>
                        <div className="relative">
                          <ClockIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#6B7280] dark:text-[#9CA3AF]" />
                          <input name="entryTime" type="text" value={form.entryTime} onChange={handleChange} className="w-full bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] pl-10 pr-3 py-2 rounded-card border border-[#E5E7EB] dark:border-[#374151] focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6] focus:outline-none transition-all text-sm font-normal" placeholder="e.g. 09:30" />
                        </div>
                      </div>
                      <div className="hidden sm:block"></div>
                    </div>
                    {/* Exit Date/Time for Swing Trading */}
                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                      <div className="flex flex-col gap-2">
                        <label className="text-xs font-medium text-[#374151] dark:text-[#D1D5DB]">Exit Date</label>
                        <div className="relative">
                          <CalendarIcon className="absolute left-3 top-1/2 hidden sm:block transform -translate-y-1/2 w-4 h-4 text-[#6B7280] dark:text-[#9CA3AF]" />
                          <input 
                            type="date" 
                            value={exitDate} 
                            onChange={handleExitDateChange}
                            className="w-full bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] pl-3 sm:pl-10 pr-3 py-2 rounded-card border border-[#E5E7EB] dark:border-[#374151] focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6] focus:outline-none transition-all text-sm font-normal [&::-webkit-calendar-picker-indicator]:cursor-pointer dark:[&::-webkit-calendar-picker-indicator]:invert"
                          />
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className="text-xs font-medium text-[#374151] dark:text-[#D1D5DB]">Exit Time</label>
                        <div className="relative">
                          <ClockIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#6B7280] dark:text-[#9CA3AF]" />
                          <input name="exitTime" type="text" value={form.exitTime} onChange={handleChange} className="w-full bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] pl-10 pr-3 py-2 rounded-card border border-[#E5E7EB] dark:border-[#374151] focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6] focus:outline-none transition-all text-sm font-normal" placeholder="e.g. 14:45" />
                        </div>
                      </div>
                      <div data-testid="duration-field" className="col-span-2 flex flex-col gap-2 sm:col-span-1">
                        <div className="flex items-center justify-between gap-2">
                          <label htmlFor="edit-duration" className="text-xs font-medium text-[#374151] dark:text-[#9CA3AF]">Duration</label>
                          {durationSuggestion && (
                            <button type="button" onClick={applySuggestedDuration} className="text-xs font-medium text-[#3B82F6] hover:underline dark:text-[#60A5FA]">
                              Use {durationSuggestion}
                            </button>
                          )}
                        </div>
                        <input id="edit-duration" name="duration" type="text" value={form.duration} onChange={handleDurationChange} className="w-full bg-[#F9FAFB] dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] p-2 rounded-card border border-[#E5E7EB] dark:border-[#374151] focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6] focus:outline-none transition-all text-sm font-normal" placeholder="Auto-calculated" />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Screenshots */}
            <div className="mb-6">
              <div className="bg-white dark:bg-[#1F2937] rounded-card p-6 border border-[#E5E7EB] dark:border-[#374151] shadow-subtle">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <PhotoIcon className="w-5 h-5 text-[#3B82F6]" />
                      <h3 className="text-lg font-semibold text-[#111827] dark:text-[#F9FAFB]">Screenshots</h3>
                    </div>
                    <p className="text-sm text-[#6B7280] dark:text-[#9CA3AF] mt-1">
                      Add more chart images or remove any before saving this edit.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="inline-flex items-center justify-center px-4 py-2 rounded-card border border-[#D1D5DB] dark:border-[#4B5563] bg-[#F9FAFB] dark:bg-[#111827] text-[#111827] dark:text-[#F9FAFB] hover:bg-[#F3F4F6] dark:hover:bg-[#374151] transition-colors"
                  >
                    Add Chart
                  </button>
                </div>

                <div
                  className={`flex flex-col border-2 border-dashed border-[#E5E7EB] dark:border-[#374151] rounded-card transition-colors cursor-pointer ${
                    screenshots.length === 0 ? 'min-h-32 justify-center' : 'p-4'
                  } ${screenshots.length > 0 ? 'hover:border-[#3B82F6]' : ''}`}
                  style={isDraggingChart ? CHART_DROP_HIGHLIGHT_STYLE : undefined}
                  data-dragging={isDraggingChart ? 'true' : undefined}
                  {...chartDropZoneProps}
                  onPaste={handlePasteImages}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleFileChange}
                    className="hidden"
                  />

                  <input
                    type="text"
                    aria-label="Paste screenshots"
                    value=""
                    onChange={() => {}}
                    onClick={(e) => e.stopPropagation()}
                    onPaste={handlePasteImages}
                    placeholder="Click here, then Ctrl+V to paste images"
                    className="mx-3 mb-3 min-w-0 self-stretch rounded-card border border-[#D1D5DB] bg-white px-3 py-2 text-xs text-[#111827] outline-none focus:border-[#3B82F6] focus:ring-2 focus:ring-[#3B82F6]/20 dark:border-[#4B5563] dark:bg-[#111827] dark:text-[#F9FAFB]"
                  />

                  {screenshots.length === 0 ? (
                    <div className="flex flex-col items-center px-4 py-3 text-center">
                      <PhotoIcon className="mb-2 h-6 w-6 text-[#3B82F6] dark:text-[#60A5FA]" />
                      <div className="text-sm font-semibold text-[#111827] dark:text-[#F9FAFB]">No charts attached yet</div>
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          fileInputRef.current?.click();
                        }}
                        className="mt-2 rounded-lg bg-[#3B82F6] px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#2563EB]"
                      >
                        Add Entry or Exit Chart
                      </button>
                      <div className="mt-2 text-xs text-[#6B7280] dark:text-[#9CA3AF]">Click, drag, or paste an image here.</div>
                    </div>
                  ) : (
                    <div className="overflow-x-auto max-h-[180px]">
                      <div className="flex gap-3">
                        {screenshots.map((src, index) => (
                          <div key={`${src}-${index}`} className="relative group flex-shrink-0">
                            <img
                              src={src}
                              alt={`Screenshot ${index + 1}`}
                              className="h-36 w-auto object-cover rounded-card border border-[#E5E7EB] dark:border-[#374151] shadow-subtle hover:border-[#3B82F6] transition-all duration-300 cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedImageIndex(index);
                                setShowImageViewer(true);
                              }}
                            />
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveScreenshot(index);
                              }}
                              className="absolute top-1 right-1 bg-[#EF4444] hover:bg-[#DC2626] text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shadow-subtle opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Remove image"
                            >
                              <XMarkIcon className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Trade Session and Economic Release */}
            <div className="mb-6">
              <div className="bg-white dark:bg-[#1F2937] rounded-card p-6 border border-[#E5E7EB] dark:border-[#374151] shadow-subtle">
                <h3 className="text-lg font-semibold text-[#111827] dark:text-[#F9FAFB] mb-4">Trade Details</h3>
                <div className="space-y-4">
                  {sessionField?.enabled !== false && <div>
                    <label className="block text-sm font-medium text-[#374151] dark:text-[#D1D5DB] mb-2">{sessionField?.label || 'Session'}</label>
                    <ConfigurableJournalInput field={sessionField} name="tradeSession" value={form.tradeSession} onChange={(value) => setForm((current) => ({ ...current, tradeSession: value }))} className="w-full bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] p-3 rounded-card border border-[#E5E7EB] dark:border-[#374151] focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6] focus:outline-none transition-all text-sm font-normal" />
                  </div>}
                  {economicReleaseField?.enabled !== false && <div>
                    <label className="block text-sm font-medium text-[#374151] dark:text-[#D1D5DB] mb-2">{economicReleaseField?.label || 'Economic release'}</label>
                    <ConfigurableJournalInput field={economicReleaseField} value={economicRelease} onChange={setEconomicRelease} className="w-full bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] p-3 rounded-card border border-[#E5E7EB] dark:border-[#374151] focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6] focus:outline-none transition-all text-sm font-normal" placeholder="e.g. CPI 8:30, FOMC 14:00" />
                  </div>}
                </div>
              </div>
            </div>

            {/* Trade Environment Accordion */}
            <div className="mt-4">
              <Accordion 
                title="Trade Environment" 
                defaultOpen={false}
              >
                <div className="grid grid-cols-2 gap-4">
                  {tradeEnvFields
                    .filter(field => field.enabled)
                    .map(field => {
                      const fieldType = field.fieldType || 'yesNoNA';
                      
                      if (fieldType === 'text') {
                        return (
                          <div key={field.id} className="flex flex-col gap-2">
                            <label className="text-sm font-medium text-[#374151] dark:text-[#D1D5DB]">{field.label}</label>
                            <input
                              type="text"
                              value={tradeEnv[field.id] || ''}
                              onChange={(e) => setTradeEnv(te => ({ ...te, [field.id]: e.target.value }))}
                              className="w-full bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] p-2 rounded-card border border-[#E5E7EB] dark:border-[#374151] focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6] focus:outline-none transition-all text-sm font-normal"
                              inputMode={field.inputMode || undefined}
                              placeholder={field.placeholder || 'Enter text...'}
                            />
                          </div>
                        );
                      } else if (fieldType === 'dropdown') {
                        const options = field.options || [];
                        return (
                          <div key={field.id} className="flex flex-col gap-2">
                            <label className="text-sm font-medium text-[#374151] dark:text-[#D1D5DB]">{field.label}</label>
                            <select
                              value={tradeEnv[field.id] || ''}
                              onChange={(e) => setTradeEnv(te => ({ ...te, [field.id]: e.target.value }))}
                              className="w-full bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] p-2 rounded-card border border-[#E5E7EB] dark:border-[#374151] focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6] focus:outline-none transition-all text-sm font-normal"
                          >
                            <option value="">{field.placeholder || 'Select an option'}</option>
                            {options.length > 0 ? (
                                options.map(opt => (
                                  <option key={opt} value={opt}>{opt}</option>
                                ))
                              ) : (
                                <option value="">No options defined</option>
                              )}
                            </select>
                          </div>
                        );
                      } else {
                        return (
                          <SegmentedPill
                            key={field.id}
                            value={tradeEnv[field.id]}
                            onChange={v => setTradeEnv(te => ({ ...te, [field.id]: v }))}
                            label={field.label}
                          />
                        );
                      }
                    })}
                </div>
              </Accordion>
            </div>

            {/* Targets Section Accordion */}
            <div className="mt-4">
              <Accordion 
                title="Targets" 
                defaultOpen={false}
              >
                <div className="space-y-4">
                  {pdArrayField?.enabled !== false && <div>
                    <label className="block text-sm font-medium text-[#374151] dark:text-[#D1D5DB] mb-2">
                      {pdArrayField?.label || 'Entry model / PD array'}
                    </label>
                    <ConfigurableJournalInput
                      field={pdArrayField}
                      value={pdArray}
                      onChange={setPdArray}
                      className="w-full bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] p-3 rounded-card border border-[#E5E7EB] dark:border-[#374151] focus:ring-2 focus:ring-[var(--theme-success)] focus:border-[var(--theme-success)] focus:outline-none transition-all text-sm font-normal"
                      placeholder="Enter PD array..."
                    />
                  </div>}
                  {targetField?.enabled !== false && <div>
                    <label className="block text-sm font-medium text-[#374151] dark:text-[#D1D5DB] mb-2">
                      {targetField?.label || 'Target'}
                    </label>
                    <ConfigurableJournalInput
                      field={targetField}
                      value={target}
                      onChange={setTarget}
                      className="w-full bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] p-3 rounded-card border border-[#E5E7EB] dark:border-[#374151] focus:ring-2 focus:ring-[var(--theme-success)] focus:border-[var(--theme-success)] focus:outline-none transition-all text-sm font-normal"
                      placeholder="e.g. BSL/SSL, PD array, HTF level, daily high/low..."
                    />
                  </div>}
                </div>
              </Accordion>
            </div>

            {/* Trade Notes */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-[#374151] dark:text-[#D1D5DB] mb-2">Trade Notes</label>
              <textarea 
                name="notes" 
                value={form.notes} 
                onChange={handleChange} 
                className="w-full bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] p-4 rounded-card border border-[#E5E7EB] dark:border-[#374151] focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6] focus:outline-none transition-all resize-y min-h-[120px] text-sm font-normal leading-relaxed" 
                placeholder="Enter your trade notes here..."
              />
            </div>
          </div>
        </div>

        {/* Sticky Bottom Action Bar */}
        <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-[#1F2937] border-t border-[#E5E7EB] dark:border-[#374151] p-4 z-[70] flex justify-end items-center gap-4">
          <motion.button 
            whileHover={{ scale: 1.04 }} 
            whileTap={{ scale: 0.98 }} 
            type="button" 
            className="btn-secondary" 
            onClick={handleClose}
            disabled={saving}
          >
            Cancel
          </motion.button>
          <motion.button 
            whileHover={{ scale: 1.04 }} 
            whileTap={{ scale: 0.98 }} 
            type="submit" 
            className="btn-primary" 
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </motion.button>
        </div>
      </motion.div>
      </form>

      <ZoomableImageModal
        isOpen={showImageViewer && screenshots.length > 0}
        images={screenshots}
        index={selectedImageIndex}
        onClose={() => setShowImageViewer(false)}
        onPrev={() => setSelectedImageIndex((prev) => (prev > 0 ? prev - 1 : screenshots.length - 1))}
        onNext={() => setSelectedImageIndex((prev) => (prev < screenshots.length - 1 ? prev + 1 : 0))}
        showNavigation={screenshots.length > 1}
      />
    </>
  );
};

export default EditTradePage;

